import { sv } from 'date-fns/locale';
import React, { useEffect, useMemo } from 'react';
import { Controller, useForm, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { GeodataProcessType } from '../../../../../../typings/api/skymap/rest/v1/.common';
import { formattedDate } from '../../../../utils/dateUtils';
import { isValidNumber } from '../../../../utils/math/number';
import { GsdUnit } from '../../../../utils/proj4/units';
import { validateUploadName } from '../../../../utils/text/upload-name';
import { isDefined } from '../../../../utils/variables';
import { useGsdUnit } from '../../../hooks/use-gsd-unit';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { Select } from '../../select/select';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';

/** The default and smallest GSD: 0.8 in ≈ 0.02 m and 0.2 in ≈ 0.005 m. */
export const geodataGsdLimits: Record<GsdUnit, { defaultValue: string; minValue: number }> = {
  m: { defaultValue: '0.02', minValue: 0.005 },
  in: { defaultValue: '0.8', minValue: 0.2 },
};

type FormValues = {
  name: string;
  /** In the project's GSD unit, see `useGsdUnit`. */
  gsd: string;
  objMaxTriangles: string | undefined;
  rollingShutter: boolean;
};

const NameFormField = (props: {
  form: UseFormReturn<FormValues>;
  required: boolean;
  disabled: boolean;
}) => {
  const { t } = useTranslation();
  const minLength = 3;

  return (
    <LabelledContainer required={props.required} text={t('name', { ns: 'common' })}>
      {(formElementId) => {
        return (
          <Controller
            control={props.form.control}
            name="name"
            render={({ field }) => (
              <TextBox
                {...field}
                disabled={props.disabled}
                errorMessage={props.form.formState.errors.name?.message}
                id={formElementId()}
              />
            )}
            rules={{
              required: props.required,
              onChange: () => props.form.trigger('name'),
              validate: (value) => {
                const result = validateUploadName(value);
                if (!result.valid && result.illegalCharacters.length > 0) {
                  return t('setupDialog.nameContainsIllegalCharacters', {
                    ns: 'cloudProcessing',
                    illegalCharacters: result.illegalCharacters,
                  });
                }

                if (value.length < minLength) {
                  return t('setupDialog.nameIsTooShort', { ns: 'cloudProcessing' });
                }

                return true;
              },
            }}
          />
        );
      }}
    </LabelledContainer>
  );
};

const GsdFormField = (props: {
  form: UseFormReturn<FormValues>;
  required: boolean;
  disabled: boolean;
}) => {
  const { t } = useTranslation();
  const gsdUnit = useGsdUnit();
  const { minValue } = geodataGsdLimits[gsdUnit];
  const unitPerPixel = `${gsdUnit}/px`;

  return (
    <LabelledContainer
      infoTooltip={
        <Stack spacing={0.5}>
          <span>
            <strong>{t('settings.gsd', { ns: 'cloudProcessing', unit: unitPerPixel })}</strong>
          </span>
          <span>{t('settings.gsdInfo', { ns: 'cloudProcessing' })}</span>
        </Stack>
      }
      required={props.required}
      text={t('settings.gsd', { ns: 'cloudProcessing', unit: unitPerPixel })}
    >
      {(formElementId) => {
        return (
          <Controller
            control={props.form.control}
            name="gsd"
            render={({ field }) => (
              <TextBox
                {...field}
                disabled={props.disabled}
                errorMessage={props.form.formState.errors.gsd?.message}
                id={formElementId()}
                type="number"
                width={250}
              />
            )}
            rules={{
              required: props.required,
              onChange: () => props.form.trigger('gsd'),
              validate: (value) => {
                if (!isValidNumber(value) || Number(value) < minValue) {
                  return t('settings.gsdError', {
                    ns: 'cloudProcessing',
                    minValue,
                    unit: unitPerPixel,
                  });
                }

                const decimalPart = String(Number(value)).split('.')[1];
                if (decimalPart && decimalPart.length > 3) {
                  return t('settings.gsdError', {
                    ns: 'cloudProcessing',
                    minValue,
                    unit: unitPerPixel,
                  });
                }

                return true;
              },
            }}
          />
        );
      }}
    </LabelledContainer>
  );
};

const RollingShutterFormField = (props: {
  form: UseFormReturn<FormValues>;
  required: boolean;
  disabled: boolean;
}) => {
  const { t } = useTranslation();
  const yesNoOptions = useMemo(
    () => [
      { id: 'yes', name: t('yes', { ns: 'common' }) },
      { id: 'no', name: t('no', { ns: 'common' }) },
    ],
    [t],
  );

  return (
    <LabelledContainer
      infoTooltip={
        <Stack spacing={0.5}>
          <span>
            <strong>{t('setupDialog.rollingShutter', { ns: 'cloudProcessing' })}</strong>
          </span>
          <span>{t('settings.rollingShutterInfo', { ns: 'cloudProcessing' })}</span>
        </Stack>
      }
      required={props.required}
      text={t('setupDialog.rollingShutter', { ns: 'cloudProcessing' })}
    >
      {(formElementId) => {
        return (
          <Controller
            control={props.form.control}
            name="rollingShutter"
            render={({ field }) => (
              <Select
                {...field}
                className="form-select"
                disabled={props.disabled}
                id={formElementId()}
                options={yesNoOptions}
                value={field.value ? 'yes' : 'no'}
                width={250}
                onChange={async (e) => {
                  field.onChange(e.target.value === 'yes');
                  await props.form.trigger('rollingShutter');
                }}
              />
            )}
            rules={{
              required: props.required,
              onChange: () => props.form.trigger('rollingShutter'),
            }}
          />
        );
      }}
    </LabelledContainer>
  );
};

const ObjMaxTrianglesFormField = (props: {
  form: UseFormReturn<FormValues>;
  required: boolean;
  disabled: boolean;
  currentValue?: number;
}) => {
  const { t } = useTranslation();
  const minValue = 100;

  return (
    <LabelledContainer
      infoTooltip={
        <Stack spacing={0.5}>
          <span>
            <strong>{t('settings.objMaxTriangles', { ns: 'cloudProcessing' })}</strong>
          </span>
          <span>{t('settings.objMaxTrianglesInfo', { ns: 'cloudProcessing' })}</span>
        </Stack>
      }
      required={props.required}
      text={t('settings.objMaxTriangles', { ns: 'cloudProcessing' })}
    >
      {(formElementId) => {
        return (
          <Controller
            control={props.form.control}
            name="objMaxTriangles"
            render={({ field }) => (
              <TextBox
                {...field}
                disabled={props.disabled}
                errorMessage={props.form.formState.errors.objMaxTriangles?.message}
                id={formElementId()}
                placeholder={
                  props.currentValue?.toString() ??
                  t('settings.objMaxTrianglesNoLimit', { ns: 'cloudProcessing' })
                }
                step={1}
                type="number"
                width={250}
              />
            )}
            rules={{
              required: props.required,
              onChange: () => props.form.trigger('objMaxTriangles'),
              validate: (value) => {
                if (!isDefined(value) || value.trim().length === 0) {
                  return true;
                }

                if (!isValidNumber(value) || Number(value) < minValue) {
                  return t('settings.objMaxTrianglesError', {
                    ns: 'cloudProcessing',
                    minValue,
                  });
                }

                return true;
              },
            }}
          />
        );
      }}
    </LabelledContainer>
  );
};

export type GeodataConfigurationData = FormValues;

export type GeodataConfigurationChangeData = {
  formData: GeodataConfigurationData;
  valid: boolean;
};

type GeodataConfigurationProps = {
  defaultValues: GeodataConfigurationData;
  processType: GeodataProcessType;
  onChanged: (data: GeodataConfigurationChangeData) => void;
};

function getDefaultName() {
  return formattedDate(Date.now(), sv);
}

function getNormalizedDefaultValues(
  dv: GeodataConfigurationData | undefined,
  defaultGsd: string,
): FormValues {
  return {
    name: dv?.name ?? getDefaultName(),
    gsd: dv?.gsd != null ? String(dv.gsd) : defaultGsd,
    rollingShutter: dv?.rollingShutter ?? false,
    objMaxTriangles: dv?.objMaxTriangles != null ? String(dv.objMaxTriangles) : undefined,
  };
}

/**
 * Component for configuring the Geodata.
 * Here it is possible to update GCP points, settings and addons for the process.
 */
export const GeodataConfiguration = (props: GeodataConfigurationProps) => {
  const defaultGsd = geodataGsdLimits[useGsdUnit()].defaultValue;
  const initialDefaultValues = useMemo(
    () => getNormalizedDefaultValues(props.defaultValues, defaultGsd),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only use initial props for first paint so the field is not empty on load
    [],
  );

  const form = useForm<FormValues>({
    defaultValues: initialDefaultValues,
  });

  // 1) Reinitialize form when defaults change (from external source only — avoid reset when parent echoes our own data to prevent clearing errors)
  const skipNextEmitRef = React.useRef(true);
  useEffect(() => {
    const nextValues = getNormalizedDefaultValues(props.defaultValues, defaultGsd);
    const current = form.getValues();
    const same =
      (current.name ?? getDefaultName()) === nextValues.name &&
      (current.gsd ?? defaultGsd) === nextValues.gsd &&
      (current.rollingShutter ?? false) === nextValues.rollingShutter &&
      (current.objMaxTriangles ?? undefined) === (nextValues.objMaxTriangles ?? undefined);

    if (same) {
      return;
    }

    form.reset(nextValues);

    // Skip the next emission after reset (useWatch will fire)
    skipNextEmitRef.current = true;
  }, [props.defaultValues, form, defaultGsd]);

  // 2) Watch values (more efficient than form.watch() in render)
  const values = useWatch({ control: form.control });

  // Subscribe to formState so we re-render when validity/errors change
  const { isValid } = form.formState;

  // 3) Emit changes, but avoid emitting immediately after reset (and avoid props in deps)
  const { onChanged } = props;
  useEffect(() => {
    // After reset(), useWatch will fire; skip that one
    if (skipNextEmitRef.current) {
      skipNextEmitRef.current = false;
      return;
    }

    onChanged({
      formData: {
        name: values.name ?? getDefaultName(),
        gsd: values.gsd ?? defaultGsd,
        objMaxTriangles: values.objMaxTriangles,
        rollingShutter: values.rollingShutter ?? false,
      },
      valid: isValid,
    });
  }, [values, isValid, onChanged, defaultGsd]);

  return (
    <Form id="geodataConfigurationForm">
      <Stack spacing={1}>
        <NameFormField disabled={false} form={form} required={false} />

        {props.processType !== 'PM' && (
          <GsdFormField disabled={false} form={form} required={false} />
        )}

        {props.processType !== 'PM' && (
          <RollingShutterFormField disabled={false} form={form} required={false} />
        )}

        {props.processType === 'FULL_HIGH_QUALITY' && (
          <ObjMaxTrianglesFormField
            currentValue={
              props.defaultValues?.objMaxTriangles
                ? Number(props.defaultValues.objMaxTriangles)
                : undefined
            }
            disabled={false}
            form={form}
            required={false}
          />
        )}
      </Stack>
    </Form>
  );
};

const Form = styled.form`
  min-width: 450px;
`;
